/**********************************************************************************************************
 *   BASE IMPORT
 **********************************************************************************************************/
import { Dispatch, MutableRefObject, RefObject, SetStateAction, useCallback, useEffect, useRef, useState } from 'react'

/**********************************************************************************************************
 *   TYPE
 **********************************************************************************************************/
type SetValue<T> = Dispatch<SetStateAction<T>>

/**********************************************************************************************************
 *   HOOK
 **********************************************************************************************************/
function useIsMounted() {
    const isMounted = useRef(false)

    useEffect(() => {
        isMounted.current = true

        return () => {
            isMounted.current = false
        }
    }, [])

    return useCallback(() => isMounted.current, [])
}

function useEventListener<T extends HTMLElement = HTMLDivElement>(
    // eslint-disable-next-line no-undef
    eventName: keyof WindowEventMap | string,
    handler: (event: Event) => void,
    element?: RefObject<T>
) {
    const saveHandler = useRef<(event: Event) => void>()

    useEffect(() => {
        const targetElement: T | Window = element?.current || window

        if (!(targetElement && targetElement.addEventListener)) {
            return
        }

        if (saveHandler.current !== handler) {
            saveHandler.current = handler
        }

        const eventListener = (event: Event) => {
            if (saveHandler?.current) {
                saveHandler.current(event)
            }
        }

        targetElement.addEventListener(eventName, eventListener)

        return () => {
            targetElement.removeEventListener(eventName, eventListener)
        }
    }, [eventName, element, handler])
}

function useLocalStorage<T>(key: string, initialValue: T): [T, SetValue<T>] {
    const readValue = (): T => {
        if (typeof window === 'undefined') {
            return initialValue
        }

        try {
            const item = window.localStorage.getItem(key)
            return item ? (parseJSON(item) as T) : initialValue
        } catch (error) {
            console.warn(`Error reading localStorage key "${key}":`, error)
            return initialValue
        }
    }

    const [storeValue, setStoreValue] = useState<T>(readValue)

    const setValue: SetValue<T> = value => {
        if (typeof window === 'undefined') {
            console.warn(`Tried setting localStorage key "${key}" even though environment is not a client`)
        }

        try {
            const newValue = value instanceof Function ? value(storeValue) : value

            window.localStorage.setItem(key, JSON.stringify(newValue))

            setStoreValue(newValue)

            window.dispatchEvent(new Event('local-storage'))
        } catch (error) {
            console.warn(`Error setting localStorage key "${key}":`, error)
        }
    }

    useEffect(() => {
        setStoreValue(readValue())
    }, [])

    const handleStorageChange = () => {
        setStoreValue(readValue())
    }

    useEventListener('storage', handleStorageChange)

    useEventListener('local-storage', handleStorageChange)

    return [storeValue, setValue]
}

function usePrevious<T>(value: T): MutableRefObject<T | undefined>['current'] {
    const ref = useRef<T>()

    useEffect(() => {
        ref.current = value
    }, [value])

    return ref.current
}

function parseJSON<T>(value: string | null): T | undefined {
    try {
        return value === 'undefined' ? undefined : JSON.parse(value ?? '')
    } catch (error) {
        console.log('parsing error on', { value })
        return undefined
    }
}

export {
    useIsMounted,
    useEventListener,
    useLocalStorage,
    usePrevious
}
